// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import { useEvolveGet, useEvolvePaginatedGet, type GlobalAggregatedBOMItem } from '@evolvemep/api-ts';
import { AgGridReact } from 'ag-grid-react';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import { InfoIcon } from 'components/Mantine/InfoIcon';
import { convertTimeFrameDataToRequestParams } from 'components/Mantine/TimeFramePicker/helpers';
import { AgGridContainer } from 'helpers/ag-grid/AgGridContainer';
import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import { useManufacturers } from 'hooks/typeCacheHooks/useManufacturers';
import { useUnitOfMeasure } from 'hooks/typeCacheHooks/useUnitOfMeasure';
import { getBillOfMaterialsColumnDefs } from 'modules/Shop/BillOfMaterials/columnDefs';
import type { AggregatedBOMTableAgContext } from 'modules/Shop/BillOfMaterials/types';

export const BillOfMaterialsPage = () => {
  const { projectIds, workOrderIds, timeFrame } = useGlobalFilters({
    projects: true,
    workOrders: true,
    timeFrame: {
      defaultTimeFrame: 'This Week',
    },
  });

  const { data: categories = [], setDefaultOpts: setDefaultOptsCategories } = useEvolveGet(
    'shop/workOrder/aggregateBom/categories',
  );
  const params = useMemo(
    () => ({
      projectIds,
      workOrderIds,
      ...convertTimeFrameDataToRequestParams(timeFrame),
    }),
    [projectIds, workOrderIds, timeFrame],
  );

  useEffect(() => {
    setDefaultOptsCategories({
      lazy: false,
      defaultConfig: {
        params,
      },
    });
  }, [params, setDefaultOptsCategories]);

  const colDef = useMemo(() => getBillOfMaterialsColumnDefs(categories), [categories]);

  const {
    fetchPage,
    abortController,
    setDefaultOpts: setDefaultOptsAggregateBOM,
  } = useEvolvePaginatedGet('shop/workOrder/aggregateBoM');
  useEffect(() => {
    setDefaultOptsAggregateBOM(
      {
        defaultConfig: {
          params,
        },
      },
      true,
    );
  }, [setDefaultOptsAggregateBOM, params]);

  const { uoms } = useUnitOfMeasure();
  const { manufacturers } = useManufacturers();
  const agGridContext = useMemo<AggregatedBOMTableAgContext>(
    () => ({
      uoms,
      manufacturers,
    }),
    [manufacturers, uoms],
  );

  const { agGridProps } = useServerSideGrid({
    tableName: 'aggregate-bom',
    tableViewType: TABLE_VIEW_TYPE.BILL_OF_MATERIALS,
    exportFileName: 'Bill of Materials',
    fetchPage,
    abortController,
    colDef,
    rowId: 'rowId',
    context: agGridContext,
  });

  return (
    <>
      <BasePageHeader
        title="Bill of Materials"
        topLeftComponent={
          <InfoIcon
            label="Generates a consolidated list of BOM items filtered by project, work order, and selected need-by date range."
            tooltipProps={{
              position: 'bottom',
            }}
          />
        }
      />
      <AgGridContainer>
        {(applyWrap) => <AgGridReact<GlobalAggregatedBOMItem> {...agGridProps} onFirstDataRendered={applyWrap} />}
      </AgGridContainer>
    </>
  );
};
